import {QueryClient, useQuery} from "@tanstack/react-query";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, LinearProgress, Modal, Sheet, Stack, useTheme} from "@mui/joy";
import {faExchange, faImages} from "@fortawesome/pro-regular-svg-icons";
import {useTranslation} from "react-i18next";
import {uuidv7} from "uuidv7";
import axios from "axios";
import {useMediaQuery} from "@mui/material";
import json2mq from "json2mq";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Dropzone from "react-dropzone";
import {Authenticator} from "./auth/authenticator.tsx";
import {mothershipApiBaseUrl} from "./rpc/mama.tsx";

const MAX_PHOTO_DIM = 1200;
export const MAX_THUMBNAIL_DIM = 120;

export function photoUrl(slug: string): string {
    const apiBase = mothershipApiBaseUrl();
    return `${apiBase}/photo/${slug}`;
}

export function thumbnailUrl(slug: string): string {
    const apiBase = mothershipApiBaseUrl();
    return `${apiBase}/thumbnail/${slug}`;
}

export function useHasCamera() {
    const {data: tqrv} = useQuery({
        queryKey: ["hasCamera"],
        queryFn: () => {
            return navigator.mediaDevices.enumerateDevices()
                .then(mds => {
                    for (const md of mds) {
                        if (md.kind === "videoinput") {
                            return true;
                        }
                    }
                    return false;
                })
        }
    });
    return tqrv;
}

export function useHasDragNDrop() {
    return useMediaQuery(json2mq({pointer: "fine", hover: "hover"}));
}

export interface GenericInputProps<T> {
    value: T;
    onChange: (evt: { target: { value: T } }) => void;
}

export interface LocalPhoto {
    id: string;
    file: File | Blob;
}

export type PhotoInputProps = GenericInputProps<LocalPhoto | null>;

interface Rect {
    width: number;
    height: number;
}

function renderBlob(blob: Blob): Promise<HTMLImageElement> {
    const url = window.URL.createObjectURL(blob);
    return new Promise<HTMLImageElement>((resolve, reject) => {
        try {
            const img = new Image();
            img.onload = () => {
                resolve(img);
            };
            img.src = url;
        } catch (xc) {
            reject(new Error("renderBlob failed", {cause: xc}));
        }
    }).finally(() => {
        window.URL.revokeObjectURL(url);
    });
}

// returns a Rect that matches the aspect ratio of the
// original, but with no dimension larger than max.
function fitWithin(r: Rect, max: number): Rect {
    if (r.width === 0 || r.height === 0 || (r.width <= max && r.height <= max)) {
        return r;
    }
    const landscapep = r.width > r.height;
    const aspect = r.width / r.height;
    const rv: Rect = landscapep ? {
        width: max,
        height: max / aspect,
    } : {
        width: max * aspect,
        height: max,
    };
    return rv;
}

function scale(full: Blob, maxdim: number): Promise<Blob> {
    return renderBlob(full)
        .then(imge => {
            const fullBounds: Rect = {
                width: imge.width,
                height: imge.height,
            };
            const resizedBounds = fitWithin(fullBounds, maxdim);
            // console.log(`resizing ${fullBounds.width.toString()}x${fullBounds.height.toString()} to ${resizedBounds.width.toString()}x${resizedBounds.height.toString()}`);
            const canvas: HTMLCanvasElement = document.createElement("canvas");
            canvas.width = resizedBounds.width;
            canvas.height = resizedBounds.height;
            const draw2d = canvas.getContext("2d");
            if (!draw2d) {
                return Promise.reject(new Error("can't get canvas drawing context"));
            }
            draw2d.drawImage(imge, 0, 0, resizedBounds.width, resizedBounds.height);
            return canvasToBlob(canvas);
        });
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error("canvasToBlob failed"));
            }
        });
    });
}

const useBlobURL = (seed: File | Blob | null | undefined) => {
    const [url, setUrl]
        = useState<string | null>(null);
    const [blob, setBlob]
        = useState<File | Blob | null>(seed ?? null);

    useEffect(() => {
        if (!blob) {
            return;
        }

        const newurl = URL.createObjectURL(blob);
        setUrl(newurl);

        return () => {
            URL.revokeObjectURL(newurl);
            setUrl(null);
        };
    }, [blob]);

    return {url, setBlob};
};

function scaledImageTqk(id: string, maxdim: number) {
    return ["scaleImage", id, maxdim];
}

function scaledImageTqo({lp, maxdim}: { lp: LocalPhoto, maxdim: number }) {
    return {
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: scaledImageTqk(lp.id, maxdim),
        queryFn: () => scale(lp.file, maxdim),
    }
}

export interface PhotoZoomState {
    url: string | null,
    setUrl: (url: string | null) => void,
}

export const PhotoZoomContext
    = createContext<PhotoZoomState>({
    url: null, setUrl: () => {
        /* empty */
    },
});

interface LoadedPhotoInputProps {
    lp: LocalPhoto,
    unload: () => void,
}

function LoadedPhotoInput({lp, unload}: LoadedPhotoInputProps) {
    const {t} = useTranslation();
    const {data: thumbnail, isSuccess: isThumbnailSuccess}
        = useQuery(scaledImageTqo({lp, maxdim: MAX_THUMBNAIL_DIM}));
    const {url: fullUrl} = useBlobURL(lp.file);
    const {url: thumbnailUrl, setBlob: setThumbnailBlob}
        = useBlobURL(thumbnail);
    const {setUrl: setZoomUrl} = useContext(PhotoZoomContext);

    useEffect(() => {
        if (isThumbnailSuccess && !thumbnailUrl) {
            setThumbnailBlob(thumbnail)
        }
    }, [isThumbnailSuccess, thumbnailUrl, thumbnail, setThumbnailBlob]);

    return <>
        <Stack direction="column" spacing={1}>
            {thumbnailUrl
                ? <img src={thumbnailUrl} onClick={() => {
                    setZoomUrl(fullUrl);
                }}/>
                : null}
            <Button onClick={unload} sx={{px: 4, py: 1, whiteSpace: "nowrap", mx: 0.8}}>
                <FontAwesomeIcon icon={faExchange}></FontAwesomeIcon>
                <Box sx={{ml: 0.8}}>{t("swapPhoto")}</Box>
            </Button>
        </Stack>
    </>
}

interface UnloadedPhotoInputProps {
    load: (lp: LocalPhoto) => void,
}

function UnloadedPhotoInput({load}: UnloadedPhotoInputProps) {
    const theme = useTheme();
    //   const hasCamera = useHasCamera();
//    const hasDnD = useHasDragNDrop();
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const onCameraChange = useCallback(() => {
        if (cameraInputRef.current?.files?.[0]) {
            load({id: uuidv7(), file: cameraInputRef.current.files[0]});
        }
    }, [load]);

    if (cameraInputRef.current) {
        cameraInputRef.current.onchange = onCameraChange;
    }

    const onGalleryChange = useCallback(() => {
        if (galleryInputRef.current?.files?.[0]) {
            load({id: uuidv7(), file: galleryInputRef.current.files[0]});
        }
    }, [load]);
    const galleryInputRef = useRef<HTMLInputElement>(null);
    if (galleryInputRef.current) {
        galleryInputRef.current.onchange = () => onGalleryChange;
    }
    const galleryFile = useMemo(() => {
        const files = galleryInputRef.current?.files;
        const rv = files && files.length > 0 ? files[0] : null;
        return rv;
    }, []);
    if (galleryFile) {
        load({id: uuidv7(), file: galleryFile});
    }

    return <Stack direction="row" spacing={2}>
        <Dropzone onDrop={files => {
            const file = files[0];
            if (file) {
                load({id: uuidv7(), file});
            }
        }}>
            {({getRootProps, getInputProps}) => (
                <section>
                    <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <Stack direction="column" spacing={1}>
                            <Stack direction="row" sx={{alignItems: "center"}}>
                                <FontAwesomeIcon icon={faImages} color={theme.palette.primary[600]} size="2x"/>
                            </Stack>
                        </Stack>
                    </div>
                </section>
            )}
        </Dropzone>

        {/*<Button sx={{p: 1}}*/}
        {/*        color="primary"*/}
        {/*        onClick={() => {*/}
        {/*            if (galleryInputRef.current) {*/}
        {/*                galleryInputRef.current.click();*/}
        {/*            }*/}
        {/*        }}>*/}
        {/*    <Box sx={{display: "none"}}>*/}
        {/*        <input ref={galleryInputRef} type="file" accept="image/*"/>*/}
        {/*    </Box>*/}
        {/*    <FontAwesomeIcon icon={faImages} size="2x"/>*/}
        {/*</Button>*/}

        {/*<Button sx={{p: 1, display: hasCamera ? "inline" : "none"}}*/}
        {/*        color="primary"*/}
        {/*        onClick={() => {*/}
        {/*            if (cameraInputRef.current) {*/}
        {/*                cameraInputRef.current.click();*/}
        {/*            }*/}
        {/*        }}>*/}
        {/*    <Box sx={{display: "none"}}>*/}
        {/*        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"/>*/}
        {/*    </Box>*/}
        {/*    <FontAwesomeIcon icon={faCameraRetro} size="2x"/>*/}
        {/*</Button>*/}
    </Stack>
}

export function PhotoInput(props: PhotoInputProps) {
    if (props.value) {
        return <LoadedPhotoInput lp={props.value} unload={() => {
            props.onChange({target: {value: null}});
        }}/>
    } else {
        return <UnloadedPhotoInput load={(lp) => {
            props.onChange({target: {value: lp}});
        }}/>
    }
}

export interface UploadPhotoProps {
    authenticator: Authenticator;
    queryClient: QueryClient;
    photo: LocalPhoto;
    onProgress?: (progress: number) => void;
    onComplete?: () => void;
}

export interface UploadPhotoResult {
    photoSlug: string;
}

interface UploadPhotoPostProps {
    id: string;
    taken: Date;
}

const META_PART_NAME = "meta";
const PHOTO_PART_NAME = "asset";
const THUMBNAIL_PART_NAME = "thumb";

export function uploadPhoto(
    {
        authenticator: auther,
        queryClient: tqc,
        photo,
        onProgress,
        onComplete
    }: UploadPhotoProps): Promise<UploadPhotoResult> {
    const apiBase = mothershipApiBaseUrl();
    const photoupEndpoint = `${apiBase}/photoup`;
    const authHeader = auther.getAuthorizationHeader();
    return Promise.all([
        tqc.ensureQueryData(
            scaledImageTqo({lp: photo, maxdim: MAX_PHOTO_DIM})),
        tqc.ensureQueryData(
            scaledImageTqo({lp: photo, maxdim: MAX_THUMBNAIL_DIM}))
    ]).then(([uploadBlob, thumbnailBlob]) => {
        const fd = new FormData();
        const meta: UploadPhotoPostProps = {
            id: photo.id,
            taken: new Date(),
        };
        // console.log(uploadBlob.size);
        // console.log(thumbnailBlob.size);
        fd.append(META_PART_NAME, JSON.stringify(meta));
        fd.append(PHOTO_PART_NAME, uploadBlob);
        fd.append(THUMBNAIL_PART_NAME, thumbnailBlob);
        return axios.post(photoupEndpoint, fd, {
            headers: {...authHeader},
            onUploadProgress: (evt) => {
                if (evt.total) {
                    const progress = Math.round(evt.loaded * 100 / evt.total);
                    // console.log(`internal progress ${progress.toString()}`);
                    if (onProgress) {
                        onProgress(progress);
                    }
                }
            }
        });
    }).then(() => {
        if (onComplete) {
            onComplete();
        }
        return {photoSlug: photo.id};
    });
}

//
// export function uploadCanvas(reactCanvas: SignatureCanvas): Promise<UploadPhotoResult> {
//     return Promise.resolve({
//         canvas: reactCanvas.getTrimmedCanvas(),
//         sigslug: uuidv7(),
//     }).then(({canvas, sigslug}) => {
//         return canvasToBlob(canvas)
//             .then(blob => ({blob, sigslug}));
//     }).then(({blob, sigslug}) =>
//         uploadPhoto({photo: {id: sigslug, file: blob}}));
// }

export interface PhotoUploadProps {
    photo: LocalPhoto;
    progress: number;
}

export function PhotoUpload({photo, progress}: PhotoUploadProps) {
    const {data: uploadBlob}
        = useQuery(scaledImageTqo({lp: photo, maxdim: MAX_PHOTO_DIM}));
    const {url: photoUrl} = useBlobURL(uploadBlob);

    return <Modal open={true}
                  sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
        <Sheet>
            <Stack direction="column" spacing={2} sx={{justifyContent: "center", alignItems: "center"}}>
                {photoUrl ? <img src={photoUrl}/> : null}
                <LinearProgress value={progress}/>
            </Stack>
        </Sheet>
    </Modal>
}
